import React, { FC, Fragment, Suspense, useContext, useState } from 'react';
import { getDateFnsLocale } from 'utils/format';
import { CONSTANTS } from 'config/constants';
import { Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import MainAppBar from 'components/navigation/app-bar/main-app-bar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, Fab } from '@mui/material';
import Technicians from 'pages/users/technicians.component';
import DirectoryTab from 'pages/client-directory/directory-tab.component';
import Clinics from 'pages/clinics/clinics.component';
import Dentists from 'pages/users/dentists.component';
import { useTranslation } from 'react-i18next';
import OrderList from 'pages/order-list/order-list.component';
import HistoricalOrders from 'pages/historical-orders/historical-orders.component';
import HistoricalOrderDetail from 'pages/historical-orders/historical-order-detail.component';
import OrderDetails from 'pages/order-details/order-details.component';
import Patients from 'pages/patients/patients.component';
import PatientHistory from 'pages/patients/patient-history.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import ManageInvoices from 'pages/manage-invoices/manage-invoices.component';
import CreateInvoice from 'pages/manage-invoices/create-invoice/create-invoice.component';
import DoneInvoice from 'pages/manage-invoices/done-invoice/done-invoice.component';
import InvoiceBatchDetail from 'pages/manage-invoices/invoice-batch-detail/invoice-batch-detail.component';
import InvoiceSummary from 'pages/invoice-summary/invoice-summary.component';
import Production from 'pages/production/production.component';
import LabSettings from 'pages/lab-settings/lab-settings.component';
import ClinicUsage from 'pages/clinic-settings/clinic-usage.component';
import TransportAdmin from 'pages/transport-admin/transport-admin.component';
import Notifications from 'pages/notifications/notifications.component';
import PageNotFound from './page-not-found.component';
import { socketIo } from 'context/socket';
import { useOrderChangedEvents } from 'hooks/useOrderChangedEvents';
import WorkflowStepOrders from 'pages/order-list/step-orders.component';
import FortnoxAuthContainer from 'components/fortnox/fortnox-auth-container.component';
import { permissions } from 'utils/permission';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import FortnoxAuthLandingPage from 'pages/fortnox/fortnox-auth-landing.component';
import Analytics from 'pages/elastic-dashboards/analytics.component';
import AddTransport from 'pages/transport/add-transport/add-transport.component';
import AppToaster from 'components/shared/app-toaster/app-toaster.component';
import { AppToasterContent, AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import AdminAddTransport from 'pages/transport-admin/admin-add-transport.component';
import UserSettings from 'pages/user-settings/user-settings.component';
import { useNavigate } from 'react-router-dom';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import VismaAuthContainer from 'components/visma/visma-auth-container.component';
import VismaAuthLandingPage from 'pages/visma/visma-auth-landing.component';
import Transport from 'pages/transport/transport.component';
import TransportPlanner from 'pages/transport/transport-schedule/transport-schedule.component';
import { useApolloClient, useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { helpDrawerVar } from 'apollo/reactive-variables/help-drawer';
import ClientSettings from 'pages/client-directory/clients/client-settings.component';
import DentistSettings from 'pages/client-directory/dentists/dentist-settings.component';
import { getInvoiceTemplate } from 'apollo/templates/invoices';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import HelpDrawer from 'components/navigation/help-drawer/help-drawer.component';
import AssistantErrorNudge from 'components/navigation/help-drawer/assistant/assistant-error-nudge.component';
import CreateSimpleOrder from 'pages/order-flow/simple-order/create-simple-order.component';
import ModifySimpleOrder from 'pages/order-flow/simple-order/modify-simple-order.component';
import SimpleLabs from 'pages/connected-labs/simple-labs.component';
import { AppSettingsProvider } from 'context/app-settings';
import { DenthubSetting } from 'models/denthub-settings';
import { getDenthubSettingsTemplate } from 'apollo/templates/denthub-settings';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import CreateActionOrder from 'pages/order-flow/action-order/create-action-order.component';
import ModifyActionOrder from 'pages/order-flow/action-order/modify-action-order.component';
import CreateLabOrder from 'pages/order-flow/lab-order/create-lab-order.component';
import CreateSupplierOrder from 'pages/order-flow/supplier-order/create-supplier-order.component';
import ModifySupplierOrder from 'pages/order-flow/supplier-order/modify-supplier-order.component';
import SubcontractOrder from 'pages/order-flow/subcontract-order/subcontract-order.component';
import AppBanner from 'components/shared/app-banner/app-banner.component';
import AppVersionWatcher from 'components/shared/app-version-watcher/app-version-watcher.component';
import SuperAdminBar from 'components/shared/super-admin-bar/super-admin-bar.component';
import TestDataBanner from 'components/shared/test-data-banner/test-data-banner.component';
import ModifyLabOrder from 'pages/order-flow/lab-order/modify-lab-order.component';
import LabOrderFromOrderForm from 'pages/order-flow/lab-order/lab-order-from-order-form.component';
import ManageActions from 'pages/manage-actions/manage-actions.component';
import { SessionWatcher } from './session-watcher.component';
import LabOrderFromScannerCase from 'pages/order-flow/lab-order/lab-order-from-scanner-case.component';
import SupplierSettings from 'pages/suppliers/supplier-directory/supplier-settings.component';
import Suppliers from 'pages/suppliers/suppliers.component';
import PriceRequests from 'pages/price-requests/price-requests.component';
import PriceRequestDetails from 'pages/price-requests/price-request-details.component';
import { LAB_PRICE_REQUESTS_URL } from 'components/price-requests/request/price-request-format';
import ThreeShapeAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/three-shape-auth-landing.component';
import MeditAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/medit-auth-landing.component';
import DexisAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/dexis-auth-landing.component';
import PandaAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/panda-auth-landing.component';
import DsCoreAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/ds-core-auth-landing.component';
import PandaEuAuthLandingPage from 'pages/order-list/scanner-cases/auth-container/panda-eu-auth-landing.component';
import ReleaseNotePopup from 'components/release-notes/release-note-popup/release-note-popup.component';
import ManageLabsComponent from 'pages/manage-labs/manage-labs.component';
import { LabsProvider } from 'context/labs-context';
import { Reports } from 'pages/reports/reports.component';
import { ReportsProvider } from 'context/reports-context';
import EditLab from 'pages/manage-labs/edit-lab/edit-lab.component';
import LabAvailability from 'pages/manage-labs/lab-availability/lab-availability.component';
import ProductionDashboard from 'pages/production-dashboard/production-dashboard.component';
import { DEFAULT_LOCALE_FORMAT } from 'models/locale-format';
import ReleaseNotesHistory from 'pages/release-notes-history/release-notes-history.component';

/** The legacy lab flow's modify route lives on; the new flow's page takes over. */
const LegacyModifyRedirect: FC = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/orders/modify-lab/${id}`} replace />;
};

/** DD-3840: the lab's old price-request page is a customer-list tab now; keeps ?counterpart= along. */
const LabPriceRequestsRedirect: FC = () => {
    const { search } = useLocation();
    return <Navigate to={{ pathname: LAB_PRICE_REQUESTS_URL, search }} replace />;
};

const App: FC = () => {
    const { i18n } = useTranslation();
    const { t: translate } = useTranslation(['common', 'invoices']);
    const [errorMsg, setErrorMsg] = useState('');
    const helpVisible = useReactiveVar(helpDrawerVar);
    const setHelpVisible = (visible: boolean) => helpDrawerVar(visible);
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrganization = currentOrgVar();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const [denthubSettings, setDenthubSettings] = useState<DenthubSetting>({} as DenthubSetting);
    const [appToasterContent, setAppToasterContent] = useState<AppToasterContent>({ isOpen: false, severity: 'error' });
    const isFortnoxEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.FORTNOX,
    );
    const isVismaEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.VISMA,
    );

    const socket = useContext(socketIo.getSocketContext());
    socket.on('ERROR', ({ eventName, sessionStr }) => {
        try {
            const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
            if (sessionStr === storedSessionStr) {
                setErrorMsg(eventName);
            }
        } catch (e) {
            console.error(e);
        }
    });

    const { loading } = useQuery(getDenthubSettingsTemplate, {
        onError: (error) => {
            console.log(error, 'error');
            showBoundary(error);
        },
        // App renders the AppSettingsProvider itself, so useAppSettings() here would
        // only get the context's no-op default: set the provider's own state.
        onCompleted: ({ denthubSettings }) => {
            setDenthubSettings(denthubSettings);
        },
    });

    const [getInvoice] = useLazyQuery(getInvoiceTemplate, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ invoice }) => {
            if (invoice) {
                console.log('GOT INVOICE AFTER UPDATE');
                // apolloClient.cache.evict({ id: apolloClient.cache.identify(order) });
                apolloClient.cache.writeQuery({
                    query: getInvoiceTemplate,
                    data: invoice,
                    variables: {
                        _id: invoice._id,
                    },
                });
            }
        },
        onError(error) {
            console.log(error);
        },
    });

    useOrderChangedEvents(socket);

    socket.on('INVOICE_UPDATE', ({ data }) => {
        try {
            const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
            if (data?._id && data.sessionStr === storedSessionStr) {
                getInvoice({
                    variables: {
                        _id: data._id,
                    },
                });
            }
        } catch (e) {
            console.error(e);
        }
    });

    // DD-3498: socket-server emits this to the tab that started a collective invoice
    // run once its last invoice result lands. Only that tab (matching sessionStr) toasts.
    const batchFinalizedToasts: Record<string, { severity: AppToasterContent['severity']; key: string }> = {
        COMPLETED: { severity: 'success', key: 'invoices:BATCH_FINALIZED_COMPLETED' },
        PARTIAL: { severity: 'warning', key: 'invoices:BATCH_FINALIZED_PARTIAL' },
        FAILED: { severity: 'error', key: 'invoices:BATCH_FINALIZED_FAILED' },
    };
    socket.on('INVOICE_BATCH_FINALIZED', (data) => {
        try {
            const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
            const toast = data?.status ? batchFinalizedToasts[data.status] : undefined;
            if (!data?.batchId || data.sessionStr !== storedSessionStr || !toast) {
                return;
            }
            setAppToasterContent({
                isOpen: true,
                severity: toast.severity,
                message: translate(toast.key, {
                    invoiceCount: data.invoiceCount,
                    failedInvoiceCount: data.failedInvoiceCount,
                }),
            });
        } catch (e) {
            console.error(e);
        }
    });

    const userLanguage = currentUser.localeFormat?.language ?? DEFAULT_LOCALE_FORMAT.language;
    if (userLanguage && userLanguage !== i18n.language) {
        i18n.changeLanguage(userLanguage);
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <AppSettingsProvider value={{ appSettings: denthubSettings, setAppSettings: setDenthubSettings }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                <AppToasterContext.Provider value={{ appToasterContent, setAppToasterContent }}>
                    <SessionWatcher />
                    <ReleaseNotePopup />
                    <AppVersionWatcher />
                    <AppToaster />
                    {errorMsg ? <AlertSnackbar type="error" message={translate(`common:${errorMsg}`)} /> : undefined}
                    {permissions.isLabTechnician(currentUser.type) &&
                    !permissions.isExternalTechnician(currentUser.role) &&
                    isFortnoxEnabled ? (
                        <FortnoxAuthContainer />
                    ) : undefined}
                    {permissions.isLabTechnician(currentUser.type) &&
                    !permissions.isExternalTechnician(currentUser.role) &&
                    isVismaEnabled ? (
                        <VismaAuthContainer />
                    ) : undefined}

                    <Box
                        display="flex"
                        sx={(theme: Theme) => ({
                            [theme.breakpoints.down('md')]: {
                                display: 'block',
                            },
                        })}
                    >
                        <MainAppBar />
                        <Fab
                            hidden={!helpVisible}
                            color="info"
                            aria-label="help"
                            size="small"
                            sx={{
                                position: 'fixed',
                                right: 20,
                                bottom: 20,
                                zIndex: 100,
                                display: { xs: 'none', sm: 'inline-flex' },
                                boxShadow: 7,
                            }}
                            onClick={() => setHelpVisible(true)}
                            data-test="main-app.help-button"
                        >
                            <QuestionMarkIcon />
                        </Fab>
                        <AssistantErrorNudge />

                        <Box
                            sx={(theme: Theme) => ({
                                width: '100%',
                                flexGrow: 1,
                                minWidth: 0,
                                padding: theme.spacing(3),
                                margin: 'auto',
                                marginTop: '60px',
                                marginBottom: '60px',
                                [theme.breakpoints.down('md')]: {
                                    padding: theme.spacing(1),
                                },
                            })}
                        >
                            <main>
                                <SuperAdminBar />
                                <AppBanner />
                                <TestDataBanner />
                                <Suspense fallback={null}>
                                    <Routes>
                                        {/* Clinic routes */}
                                        {permissions.isDentist(currentUser?.type) ? (
                                            <Fragment>
                                                <Route path="/clinics" Component={Clinics} />
                                                <Route path="/dentists" Component={Dentists} />
                                                <Route path="/orders/create-simple" Component={CreateSimpleOrder} />
                                                <Route path="/simple-connected-labs" Component={SimpleLabs} />
                                                {/* DD-3696: the old connect list is the Labs page's Find labs tab now. */}
                                                <Route
                                                    path="/simple-connect-to-labs"
                                                    element={<Navigate to="/simple-connected-labs" replace />}
                                                />
                                                <Route path="/orders/create-action" Component={CreateActionOrder} />
                                                <Route
                                                    path="/orders/create-action/draft/:id"
                                                    Component={CreateActionOrder}
                                                />
                                                <Route path="/action-based/manage-actions" Component={ManageActions} />
                                                <Route
                                                    path="/action-based/manage-labs"
                                                    element={
                                                        <LabsProvider>
                                                            <ManageLabsComponent />
                                                        </LabsProvider>
                                                    }
                                                />
                                                <Route
                                                    path="/action-based/manage-labs/edit-lab/:labId"
                                                    element={<EditLab />}
                                                />
                                                <Route
                                                    path="/action-based/manage-labs/availability/:labId"
                                                    element={<LabAvailability />}
                                                />
                                                {/* DD-3697 price requests (chain side) */}
                                                <Route path="/price-requests" Component={PriceRequests} />
                                                <Route path="/price-requests/:id" Component={PriceRequestDetails} />
                                                <Route
                                                    path="/3shape-auth-callback"
                                                    Component={ThreeShapeAuthLandingPage}
                                                />
                                                {/* DD-3641: clinic usage page, hidden behind a flag until launch */}
                                                {process.env.REACT_APP_ENABLE_CLINIC_USAGE === 'true' ? (
                                                    <Route path="/clinic-settings/usage" Component={ClinicUsage} />
                                                ) : undefined}
                                            </Fragment>
                                        ) : undefined}

                                        {/* Lab routes */}
                                        {permissions.isLabTechnician(currentUser?.type) ? (
                                            <Fragment>
                                                <Route
                                                    path="/orders/create"
                                                    element={<Navigate to="/orders/create-lab" replace />}
                                                />
                                                <Route path="/orders/create-lab" Component={CreateLabOrder} />
                                                <Route path="/orders/modify-lab/:id" Component={ModifyLabOrder} />
                                                <Route
                                                    path="/orders/create-from-scanner-case/:caseId"
                                                    Component={LabOrderFromScannerCase}
                                                />
                                                <Route
                                                    path="/orders/create-from-order-form/:orderFormId"
                                                    Component={LabOrderFromOrderForm}
                                                />
                                                <Route path="/orders/create/supplier" Component={CreateSupplierOrder} />
                                                <Route
                                                    path="/orders/subcontract/:parentOrderId"
                                                    Component={SubcontractOrder}
                                                />
                                                <Route
                                                    path="/orders/update/supplier/:id"
                                                    Component={ModifySupplierOrder}
                                                />
                                                <Route path="/suppliers/:tab" Component={Suppliers} />
                                                <Route
                                                    path="/supplier-directory/:id/:tab"
                                                    Component={SupplierSettings}
                                                />
                                                <Route path="/orders/update/:id" element={<LegacyModifyRedirect />} />
                                                <Route path="/technicians" Component={Technicians} />
                                                <Route path="/client-directory/:tab" Component={DirectoryTab} />
                                                <Route
                                                    path="/client-directory/clients/:id/:tab"
                                                    Component={ClientSettings}
                                                />
                                                <Route
                                                    path="/client-directory/dentists/:id/:tab"
                                                    Component={DentistSettings}
                                                />
                                                <Route path="/client-directory/:id/:tab" Component={ClientSettings} />
                                                <Route path="/invoices/create" Component={CreateInvoice} />
                                                <Route path="/invoices/done" Component={DoneInvoice} />
                                                <Route
                                                    path="/invoices/invoice-batches/:batchId"
                                                    Component={InvoiceBatchDetail}
                                                />
                                                {/* DD-3800: Workflow became the Production item's second tab. The
                                                    step links still point at /workflow/orders, so that route stays. */}
                                                <Route
                                                    path="/workflow"
                                                    element={<Navigate to="/production/workflow" replace />}
                                                />
                                                <Route path="/workflow/orders" Component={WorkflowStepOrders} />
                                                <Route
                                                    path="/production"
                                                    element={<Navigate to="/production/overview" replace />}
                                                />
                                                <Route path="/production/:tab" Component={Production} />
                                                <Route path="/production-dashboard" Component={ProductionDashboard} />
                                                <Route path="/lab-settings/:tab" Component={LabSettings} />
                                                {/* DD-3697 price requests (lab side). DD-3840: the log is the customer
                                                    list's Price adjustments tab; the old URL redirects there. */}
                                                <Route path="/price-requests" element={<LabPriceRequestsRedirect />} />
                                                <Route path="/price-requests/:id" Component={PriceRequestDetails} />
                                                <Route
                                                    path="/fortnox-auth-callback"
                                                    Component={FortnoxAuthLandingPage}
                                                />
                                                <Route path="/visma-auth-callback" Component={VismaAuthLandingPage} />
                                                <Route
                                                    path="/3shape-auth-callback"
                                                    Component={ThreeShapeAuthLandingPage}
                                                />
                                                <Route path="/medit-auth-callback" Component={MeditAuthLandingPage} />
                                                <Route path="/dexis-auth-callback" Component={DexisAuthLandingPage} />
                                                <Route path="/dexis-auth-callback" Component={DexisAuthLandingPage} />
                                                <Route path="/panda-auth-callback" Component={PandaAuthLandingPage} />
                                                <Route
                                                    path="/panda-auth-callback-eu"
                                                    Component={PandaEuAuthLandingPage}
                                                />
                                                <Route
                                                    path="/ds-core-auth-callback"
                                                    Component={DsCoreAuthLandingPage}
                                                />
                                                <Route
                                                    path="/transport/transport-planner"
                                                    Component={TransportPlanner}
                                                />
                                                <Route
                                                    path="/orders/create-action/draft/:id"
                                                    Component={CreateActionOrder}
                                                />
                                                <Route path="/transport/:tab" Component={Transport} />
                                                <Route path="/transport/add-transport/:type" Component={AddTransport} />
                                            </Fragment>
                                        ) : undefined}

                                        {/* Shared routes */}
                                        {/* DD-3687: the archive is reachable by labs and clinics
                                            alike; the backend scopes every read to the caller's
                                            own organization and refuses external users. */}
                                        <Route path="/historical-orders" Component={HistoricalOrders} />
                                        <Route
                                            path="/historical-orders/:historicalOrderId"
                                            Component={HistoricalOrderDetail}
                                        />
                                        <Route path="/order-details/:orderId/:tab" Component={OrderDetails} />
                                        <Route path="/patients/list/:tab" Component={Patients} />
                                        <Route path="/patients/:pin" Component={PatientHistory} />
                                        <Route path="/orders/:tab" Component={OrderList} />
                                        <Route path="/invoices/:tab" Component={ManageInvoices} />
                                        <Route path="/invoices/:invoiceId/summary" Component={InvoiceSummary} />
                                        <Route path="/notifications" Component={Notifications} />
                                        <Route path="/user-settings/:tab" Component={UserSettings} />
                                        <Route path="/orders/modify-simple/:id" Component={ModifySimpleOrder} />
                                        <Route path="/orders/modify-action/:id" Component={ModifyActionOrder} />
                                        <Route
                                            path="/reports"
                                            element={
                                                <ReportsProvider>
                                                    <Reports />
                                                </ReportsProvider>
                                            }
                                        />

                                        {permissions.isTechnicianAdmin(currentUser?.role) ||
                                        permissions.isDentistAdmin(currentUser?.role) ? (
                                            <Route path="/analytics/:tab" Component={Analytics} />
                                        ) : undefined}

                                        {/* Unique key to make sure we "refresh" the page if same url as the current page */}

                                        {/* Transport routes */}
                                        {permissions.isCarrier(currentUser?.role) ? (
                                            <Route path="/admin-transport/:tab" Component={TransportAdmin} />
                                        ) : undefined}
                                        {permissions.isCarrier(currentUser?.role) ? (
                                            <Route path="/admin-add-transport/:type" Component={AdminAddTransport} />
                                        ) : undefined}

                                        {/* Prototype & feature toggled routes */}
                                        {/* {CONSTANTS.FEATURE_TOGGLE ? <MockRoutes /> : undefined} */}

                                        <Route path="/release-notes-history" Component={ReleaseNotesHistory} />

                                        {/* No matching route */}
                                        <Route path="*" Component={PageNotFound} />
                                    </Routes>
                                </Suspense>
                            </main>
                        </Box>
                        <HelpDrawer helpVisible={helpVisible} onClose={() => setHelpVisible(false)} />
                    </Box>
                </AppToasterContext.Provider>
            </LocalizationProvider>
        </AppSettingsProvider>
    );
};

export default App;
